/**
 * Company-Admin Statistics Page
 *
 * Shows the COMPANY_ADMIN aggregate stats across all brokers in their company,
 * with a click-through drill-down to per-property stats for each broker.
 *
 * Endpoints used:
 *  - GET /api/events/company/brokers   (per-broker leaderboard within company)
 *  - GET /api/events/broker/stats?userId=...   (per-property drill-down for a
 *                                                broker in the same company)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useAuth } from '../../hooks/useAuth';
import { apiRequest, ApiError } from '../../utils/apiClient';
import SegmentedControl from '../../components/base/SegmentedControl';
import FigureStrip, { type Figure } from '../../components/base/FigureStrip';
import { formatAmount } from '../../utils/numberFormat';
import { BrandRule } from '../../components/brand/BrandRule';

type Period = 'day' | 'week' | 'month' | 'all';

interface BrokerRow {
  brokerUserId: string;
  brokerName: string;
  brokerEmail: string | null;
  brokerStatus: string | null;
  companyId: string | null;
  companyName: string | null;
  companyNameAr: string | null;
  totalProperties: number;
  totalViews: number;
  uniqueViewers: number;
  totalWhatsappClicks: number;
  totalPhoneClicks: number;
  totalEmailClicks: number;
  totalInquiries: number;
  totalFavorites: number;
}

interface CompanyStatsResponse {
  data: {
    period: Period;
    startDate: string | null;
    company: { id: string; name: string | null; nameAr: string | null };
    summary: {
      totalBrokers: number;
      totalProperties: number;
      totalViews: number;
      uniqueViewers: number;
      totalWhatsappClicks: number;
      totalPhoneClicks: number;
      totalEmailClicks: number;
      totalInquiries: number;
    };
    brokers: BrokerRow[];
  };
}

interface BrokerDrillProperty {
  id: string;
  title: string;
  slug: string | null;
  cityNameEn: string | null;
  cityNameAr: string | null;
  status: string;
  views: number;
  uniqueViewers: number;
  whatsappClicks: number;
  phoneClicks: number;
  inquiries: number;
  allTimeViews: number;
}

interface BrokerDrillResponse {
  data: {
    period: Period;
    summary: {
      totalProperties: number;
      totalViews: number;
      uniqueViewers: number;
      totalWhatsappClicks: number;
      totalPhoneClicks: number;
      totalInquiries: number;
    };
    properties: BrokerDrillProperty[];
  };
}

const HOT_LISTING_VIEW_THRESHOLD = 500;

const TH = 'px-3 py-3 text-center text-[0.8125rem] font-semibold text-[var(--pv-color-text-muted)]';
const TD = 'px-3 py-3 text-center tabular-nums';

function formatNumber(value: number): string {
  return formatAmount(value || 0);
}

export default function CompanyStatisticsPage() {
  const { i18n } = useTranslation('dashboard');
  const { session } = useAuth();
  const token = session?.token;
  const isRTL = i18n.language === 'ar';

  const [period, setPeriod] = useState<Period>('week');
  const [data, setData] = useState<CompanyStatsResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Drill-down state
  const [drillBroker, setDrillBroker] = useState<BrokerRow | null>(null);
  const [drillData, setDrillData] = useState<BrokerDrillResponse['data'] | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiRequest<CompanyStatsResponse>(
        `/events/company/brokers?period=${period}`,
        { token },
      );
      setData(response.data);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : isRTL
          ? 'تعذر تحميل الإحصائيات.'
          : 'Failed to load statistics.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [token, period, isRTL]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const openDrill = useCallback(
    async (broker: BrokerRow) => {
      if (!token) return;
      setDrillBroker(broker);
      setDrillData(null);
      setDrillError(null);
      setDrillLoading(true);
      try {
        const response = await apiRequest<BrokerDrillResponse>(
          `/events/broker/stats?period=${period}&userId=${encodeURIComponent(broker.brokerUserId)}`,
          { token },
        );
        setDrillData(response.data);
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : isRTL
            ? 'تعذر تحميل بيانات الوسيط.'
            : 'Failed to load broker details.';
        setDrillError(message);
      } finally {
        setDrillLoading(false);
      }
    },
    [token, period, isRTL],
  );

  const closeDrill = useCallback(() => {
    setDrillBroker(null);
    setDrillData(null);
    setDrillError(null);
  }, []);

  const periodOptions: { value: Period; label: string }[] = useMemo(
    () => [
      { value: 'day', label: isRTL ? 'اليوم' : 'Today' },
      { value: 'week', label: isRTL ? 'آخر 7 أيام' : 'Last 7 days' },
      { value: 'month', label: isRTL ? 'آخر 30 يوم' : 'Last 30 days' },
      { value: 'all', label: isRTL ? 'كل الوقت' : 'All time' },
    ],
    [isRTL],
  );

  // The top line. Labels stay up while the numbers load, so the page keeps its
  // shape when the period changes.
  const figures = useMemo<Figure[]>(() => {
    const s = data?.summary;
    const n = (value: number | undefined) => formatNumber(value ?? 0);
    return [
      { key: 'brokers', label: isRTL ? 'الوسطاء' : 'Brokers', value: n(s?.totalBrokers) },
      { key: 'properties', label: isRTL ? 'العقارات' : 'Properties', value: n(s?.totalProperties) },
      {
        key: 'views',
        label: isRTL ? 'المشاهدات' : 'Views',
        value: n(s?.totalViews),
        note: s ? `${n(s.uniqueViewers)} ${isRTL ? 'زائر فريد' : 'unique'}` : undefined,
      },
      { key: 'whatsapp', label: isRTL ? 'نقرات واتساب' : 'WhatsApp clicks', value: n(s?.totalWhatsappClicks) },
      { key: 'phone', label: isRTL ? 'نقرات الاتصال' : 'Phone clicks', value: n(s?.totalPhoneClicks) },
      { key: 'inquiries', label: isRTL ? 'الاستفسارات' : 'Inquiries', value: n(s?.totalInquiries) },
    ];
  }, [data, isRTL]);

  // The drill-down shows a broker what they see on their own statistics page.
  const drillFigures = useMemo<Figure[]>(() => {
    const s = drillData?.summary;
    const n = (value: number | undefined) => formatNumber(value ?? 0);
    return [
      {
        key: 'views',
        label: isRTL ? 'المشاهدات' : 'Views',
        value: n(s?.totalViews),
        note: s ? `${n(s.uniqueViewers)} ${isRTL ? 'زائر فريد' : 'unique'}` : undefined,
      },
      { key: 'whatsapp', label: isRTL ? 'نقرات واتساب' : 'WhatsApp clicks', value: n(s?.totalWhatsappClicks) },
      { key: 'phone', label: isRTL ? 'نقرات الاتصال' : 'Phone clicks', value: n(s?.totalPhoneClicks) },
      { key: 'inquiries', label: isRTL ? 'الاستفسارات' : 'Inquiries', value: n(s?.totalInquiries) },
      { key: 'properties', label: isRTL ? 'العقارات' : 'Properties', value: n(s?.totalProperties) },
    ];
  }, [drillData, isRTL]);

  const companyDisplayName = data?.company
    ? (isRTL ? data.company.nameAr || data.company.name : data.company.name) || ''
    : '';

  return (
    <div className="min-h-screen bg-[var(--pv-color-surface)]" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navigation />

      <header className="border-b border-[var(--pv-border-subtle)]">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <nav aria-label={isRTL ? 'مسار التنقل' : 'Breadcrumb'} className="flex items-center gap-2 text-[0.875rem] text-[var(--pv-color-text-muted)]">
            <Link to="/company/dashboard" className="transition-colors hover:text-[var(--pv-color-primary)]">
              {isRTL ? 'لوحة الشركة' : 'Company dashboard'}
            </Link>
            <span aria-hidden="true">/</span>
            <span className="text-[var(--pv-color-text)]">{isRTL ? 'الإحصائيات' : 'Statistics'}</span>
          </nav>
          <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-[1.5rem] font-bold text-[var(--pv-color-primary)] sm:text-[1.75rem]">
                {isRTL ? 'إحصائيات الشركة' : 'Company Statistics'}
                {companyDisplayName && (
                  <span className="ms-2 text-[1rem] font-normal text-[var(--pv-color-text-muted)]">· {companyDisplayName}</span>
                )}
              </h1>
              <BrandRule className="mt-3" />
              <p className="mt-1 text-[0.9375rem] text-[var(--pv-color-text-muted)]">
                {isRTL
                  ? 'الأداء عبر جميع الوسطاء في شركتك. انقر على وسيط لرؤية تفاصيل عقاراته.'
                  : 'Performance across every broker in your company. Click a broker to drill into per-property stats.'}
              </p>
            </div>
            <SegmentedControl
              size="sm"
              value={period}
              onChange={setPeriod}
              options={periodOptions}
              ariaLabel={isRTL ? 'الفترة' : 'Period'}
              className="self-start sm:self-auto"
            />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {error && !loading ? (
          <div role="alert" className="rounded-[16px] border border-[var(--pv-border-soft)] px-6 py-8 text-center">
            <p className="text-[0.9375rem] text-[var(--pv-color-text)]">{error}</p>
            <button
              type="button"
              onClick={fetchStats}
              className="mt-4 inline-flex h-10 items-center gap-2 rounded-full border border-[var(--pv-color-primary)] px-4 text-[0.875rem] font-semibold text-[var(--pv-color-primary)] transition-colors hover:bg-[var(--pv-color-surface-sunken)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30"
            >
              <i className="ri-refresh-line" aria-hidden="true" />
              {isRTL ? 'إعادة المحاولة' : 'Retry'}
            </button>
          </div>
        ) : (
          <>
            <FigureStrip figures={figures} loading={loading || !data} />

            {loading || !data ? (
              <div aria-hidden="true" className="mt-10 space-y-2">
                {Array.from({ length: 5 }, (_, i) => (
                  <div key={i} className="h-14 animate-pulse rounded-lg bg-[var(--pv-color-surface-sunken)] motion-reduce:animate-none" />
                ))}
              </div>
            ) : data.brokers.length === 0 ? (
              <div className="mt-10 rounded-[16px] border border-[var(--pv-border-soft)] px-6 py-10 text-center">
                <p className="text-[1rem] font-semibold text-[var(--pv-color-text)]">
                  {isRTL ? 'لا يوجد وسطاء في شركتك بعد' : 'No brokers in your company yet'}
                </p>
              </div>
            ) : (
              <section className="mt-10" aria-labelledby="stats-brokers">
                <div className="flex items-baseline justify-between gap-4">
                  <h2 id="stats-brokers" className="text-[1.125rem] font-semibold text-[var(--pv-color-primary)]">
                    {isRTL ? 'الوسطاء' : 'Brokers'}
                  </h2>
                  <span className="text-[0.875rem] tabular-nums text-[var(--pv-color-text-muted)]">
                    {isRTL
                      ? `${formatNumber(data.brokers.length)} وسيط`
                      : `${formatNumber(data.brokers.length)} broker${data.brokers.length === 1 ? '' : 's'}`}
                  </span>
                </div>

                <div className="mt-3 overflow-x-auto rounded-[16px] border border-[var(--pv-border-soft)]">
                  <table className="w-full min-w-[46rem] border-collapse text-[0.9375rem] text-[var(--pv-color-text)]">
                    <thead className="bg-[var(--pv-color-surface-sunken)]">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-start text-[0.8125rem] font-semibold text-[var(--pv-color-text-muted)]">{isRTL ? 'الوسيط' : 'Broker'}</th>
                        <th scope="col" className={TH}>{isRTL ? 'عقارات' : 'Listings'}</th>
                        <th scope="col" className={TH}>{isRTL ? 'مشاهدات' : 'Views'}</th>
                        <th scope="col" className={TH}>{isRTL ? 'فريد' : 'Unique'}</th>
                        <th scope="col" className={TH}>{isRTL ? 'واتساب' : 'WhatsApp'}</th>
                        <th scope="col" className={TH}>{isRTL ? 'اتصال' : 'Phone'}</th>
                        <th scope="col" className={TH}>{isRTL ? 'استفسارات' : 'Inquiries'}</th>
                        <th scope="col" className="w-10"><span className="sr-only">{isRTL ? 'التفاصيل' : 'Details'}</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.brokers.map((b) => (
                        <tr
                          key={b.brokerUserId}
                          onClick={() => openDrill(b)}
                          className="cursor-pointer border-t border-[var(--pv-border-subtle)] transition-colors hover:bg-[var(--pv-color-surface-soft)]"
                        >
                          <td className="px-4 py-3">
                            {/* A real button, so the drill-down opens from the keyboard too. */}
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                openDrill(b);
                              }}
                              className="rounded text-start font-medium transition-colors hover:text-[var(--pv-color-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30"
                            >
                              {b.brokerName || '-'}
                            </button>
                            {b.brokerEmail && (
                              <div className="mt-0.5 text-[0.8125rem] text-[var(--pv-color-text-muted)]">{b.brokerEmail}</div>
                            )}
                          </td>
                          <td className={TD}>{formatNumber(b.totalProperties)}</td>
                          <td className={TD}>{formatNumber(b.totalViews)}</td>
                          <td className={`${TD} text-[var(--pv-color-text-muted)]`}>{formatNumber(b.uniqueViewers)}</td>
                          <td className={TD}>{formatNumber(b.totalWhatsappClicks)}</td>
                          <td className={TD}>{formatNumber(b.totalPhoneClicks)}</td>
                          <td className={TD}>{formatNumber(b.totalInquiries)}</td>
                          <td className="px-3 py-3 text-center text-[var(--pv-color-text-muted)]">
                            <i className={isRTL ? 'ri-arrow-left-s-line' : 'ri-arrow-right-s-line'} aria-hidden="true" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {/* Drill-down modal */}
      {drillBroker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeDrill}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="drill-title"
            className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-[20px] bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-[var(--pv-border-subtle)] px-6 py-4">
              <div>
                <h3 id="drill-title" className="text-[1.125rem] font-bold text-[var(--pv-color-text)]">{drillBroker.brokerName}</h3>
                <p className="mt-0.5 text-[0.8125rem] text-[var(--pv-color-text-muted)]">
                  {drillBroker.brokerEmail || ''}
                  {drillBroker.companyName && <> · {drillBroker.companyName}</>}
                </p>
              </div>
              <button
                type="button"
                onClick={closeDrill}
                className="grid h-9 w-9 place-items-center rounded-full text-[var(--pv-color-text-muted)] transition-colors hover:bg-[var(--pv-color-surface-sunken)] hover:text-[var(--pv-color-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30"
                aria-label={isRTL ? 'إغلاق' : 'Close'}
              >
                <i className="ri-close-line text-[1.25rem] leading-none" aria-hidden="true" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {drillError && !drillLoading ? (
                <p role="alert" className="text-center text-[0.9375rem] text-[var(--pv-color-text)]">{drillError}</p>
              ) : (
                <>
                  <FigureStrip figures={drillFigures} loading={drillLoading || !drillData} />

                  {drillLoading || !drillData ? (
                    <div aria-hidden="true" className="mt-6 space-y-2">
                      {Array.from({ length: 4 }, (_, i) => (
                        <div key={i} className="h-12 animate-pulse rounded-lg bg-[var(--pv-color-surface-sunken)] motion-reduce:animate-none" />
                      ))}
                    </div>
                  ) : drillData.properties.length === 0 ? (
                    <p className="py-6 text-center text-[0.9375rem] text-[var(--pv-color-text-muted)]">
                      {isRTL ? 'لا توجد عقارات لهذا الوسيط.' : 'This broker has no properties.'}
                    </p>
                  ) : (
                    <div className="mt-6 overflow-x-auto rounded-[16px] border border-[var(--pv-border-soft)]">
                      <table className="w-full min-w-[44rem] border-collapse text-[0.875rem] text-[var(--pv-color-text)]">
                        <thead className="bg-[var(--pv-color-surface-sunken)]">
                          <tr>
                            <th scope="col" className="px-4 py-2.5 text-start text-[0.8125rem] font-semibold text-[var(--pv-color-text-muted)]">{isRTL ? 'العقار' : 'Property'}</th>
                            <th scope="col" className={TH}>{isRTL ? 'حالة' : 'Status'}</th>
                            <th scope="col" className={TH}>{isRTL ? 'مشاهدات' : 'Views'}</th>
                            <th scope="col" className={TH}>{isRTL ? 'فريد' : 'Unique'}</th>
                            <th scope="col" className={TH}>{isRTL ? 'واتساب' : 'WhatsApp'}</th>
                            <th scope="col" className={TH}>{isRTL ? 'اتصال' : 'Phone'}</th>
                            <th scope="col" className={TH}>{isRTL ? 'استفسار' : 'Inquiry'}</th>
                            <th scope="col" className={TH}>{isRTL ? 'دائم' : 'All-time'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drillData.properties.map((p) => {
                            const isHot = p.allTimeViews >= HOT_LISTING_VIEW_THRESHOLD;
                            return (
                              <tr key={p.id} className="border-t border-[var(--pv-border-subtle)] hover:bg-[var(--pv-color-surface-soft)]">
                                <td className="px-4 py-2.5">
                                  <Link to={`/property/${p.slug || p.id}`} className="flex items-center gap-2 font-medium hover:text-[var(--pv-color-primary)]">
                                    {p.title}
                                    {isHot && (
                                      <span className="rounded-full bg-[#FDE8E7] px-2 py-0.5 text-[0.75rem] font-semibold text-[#C0322A]">
                                        {isRTL ? 'شائع' : 'Hot'}
                                      </span>
                                    )}
                                  </Link>
                                  {(p.cityNameEn || p.cityNameAr) && (
                                    <div className="mt-0.5 text-[0.8125rem] text-[var(--pv-color-text-muted)]">
                                      {isRTL ? p.cityNameAr || p.cityNameEn : p.cityNameEn || p.cityNameAr}
                                    </div>
                                  )}
                                </td>
                                <td className={`${TD} text-[0.8125rem]`}>{p.status}</td>
                                <td className={TD}>{formatNumber(p.views)}</td>
                                <td className={`${TD} text-[var(--pv-color-text-muted)]`}>{formatNumber(p.uniqueViewers)}</td>
                                <td className={TD}>{formatNumber(p.whatsappClicks)}</td>
                                <td className={TD}>{formatNumber(p.phoneClicks)}</td>
                                <td className={TD}>{formatNumber(p.inquiries)}</td>
                                <td className={`${TD} text-[var(--pv-color-text-muted)]`}>{formatNumber(p.allTimeViews)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
